import React, { useState, useEffect, useCallback } from "react";
import styled, { keyframes } from "styled-components";
import { Box, Flex } from "rebass/styled-components";
import { HiExclamation } from "react-icons/hi";

import { SliderStep } from "MVVM/Hooks/useClipQualityConfigs";
import { QualitySlider } from "../QualitySlider";
import { MotionBlurTooltip } from "../MotionBlurTooltip";

type SlidersVariant = "modal" | "overlay";

interface QualitySettingsSlidersProps {
  resolutionSlider: SliderStep[];
  framerateSlider: SliderStep[];
  blurSlider: SliderStep[];
  resIdx: number;
  fpsIdx: number;
  blurIdx: number;
  onResChange: (idx: number) => void;
  onFpsChange: (idx: number) => void;
  onBlurChange: (idx: number) => void;
  onSliderRelease: () => void;
  isStepAvailableFn: (settingKey: string, value: string | number) => boolean;
  hasAnyUnavailable: boolean;
  variant?: SlidersVariant;
}

export const QualitySettingsSliders = ({
  resolutionSlider,
  framerateSlider,
  blurSlider,
  resIdx,
  fpsIdx,
  blurIdx,
  onResChange,
  onFpsChange,
  onBlurChange,
  onSliderRelease,
  isStepAvailableFn,
  hasAnyUnavailable,
  variant = "modal",
}: QualitySettingsSlidersProps) => {
  const sliderVariant = variant;
  const [showWarningPopup, setShowWarningPopup] = useState(false);
  const [popupKey, setPopupKey] = useState(0);

  const handleSliderRelease = useCallback(() => {
    onSliderRelease();
    if (hasAnyUnavailable) {
      setShowWarningPopup(true);
      setPopupKey((k) => k + 1);
    }
  }, [onSliderRelease, hasAnyUnavailable]);

  useEffect(() => {
    if (!showWarningPopup) return;
    const timer = setTimeout(() => setShowWarningPopup(false), 2500);
    return () => clearTimeout(timer);
  }, [showWarningPopup, popupKey]);

  const renderTierTag = (step: SliderStep) =>
    step.showTierTag ? (
      <TierTag $tier={step.tierRequired} $variant={variant}>
        {step.tierDisplay}
      </TierTag>
    ) : null;

  return (
    <SlidersContainer $variant={variant}>
      {showWarningPopup && (
        <UnavailablePopup key={popupKey} $variant={variant}>
          <HiExclamation size={14} /> This option is not available!
        </UnavailablePopup>
      )}
      <SliderRow $variant={variant}>
        <QualitySlider
          label="Resolution"
          steps={resolutionSlider}
          value={resIdx}
          onChange={onResChange}
          onRelease={handleSliderRelease}
          variant={sliderVariant}
          isStepAvailableFn={(val) => isStepAvailableFn("resolution", val)}
          hasAnyUnavailable={hasAnyUnavailable}
          renderStepExtra={renderTierTag}
        />
      </SliderRow>
      <SliderRow $variant={variant}>
        <QualitySlider
          label="FPS"
          steps={framerateSlider}
          value={fpsIdx}
          onChange={onFpsChange}
          onRelease={handleSliderRelease}
          variant={sliderVariant}
          isStepAvailableFn={(val) => isStepAvailableFn("framerate", val)}
          hasAnyUnavailable={hasAnyUnavailable}
          renderStepExtra={renderTierTag}
        />
      </SliderRow>
      <SliderRow $variant={variant}>
        <QualitySlider
          label="Motion Blur"
          labelExtra={<MotionBlurTooltip />}
          steps={blurSlider}
          value={blurIdx}
          onChange={onBlurChange}
          onRelease={handleSliderRelease}
          variant={sliderVariant}
          isStepAvailableFn={(val) => isStepAvailableFn("frameBlendBlur", val)}
          hasAnyUnavailable={hasAnyUnavailable}
          renderStepExtra={renderTierTag}
        />
      </SliderRow>
    </SlidersContainer>
  );
};

export const TIER_TAG_COLORS: Record<string, { bg: string; color: string }> = {
  ESSENTIALS: { bg: "rgba(78, 189, 148, 0.15)", color: "#4ebd94" },
  PRO: { bg: "rgba(0, 200, 220, 0.15)", color: "#00c8dc" },
  CREATOR_PLAT: { bg: "rgba(154, 88, 209, 0.15)", color: "#c482ff" },
};

export const TierTag = styled(Box)<{
  $tier?: string;
  $variant?: SlidersVariant;
}>`
  background: ${({ $tier }) =>
    TIER_TAG_COLORS[$tier ?? ""]?.bg ?? "rgba(78, 189, 148, 0.15)"};
  border-radius: 4px;
  color: ${({ $tier }) => TIER_TAG_COLORS[$tier ?? ""]?.color ?? "#4ebd94"};
  display: inline;
  font-size: 11px;
  font-weight: 700;
  padding: 1px 5px;
  text-transform: uppercase;
  white-space: nowrap;
`;

const SlidersContainer = styled(Flex)<{ $variant: SlidersVariant }>`
  flex-direction: column;
  gap: 0;
  position: relative;
`;

const fadeInOut = keyframes`
  0% { opacity: 0; transform: translate(-50%, -50%) translateY(4px); }
  15% { opacity: 1; transform: translate(-50%, -50%) translateY(0); }
  70% { opacity: 1; transform: translate(-50%, -50%) translateY(0); }
  100% { opacity: 0; transform: translate(-50%, -50%) translateY(0); }
`;

const UnavailablePopup = styled(Flex)<{ $variant: SlidersVariant }>`
  align-items: center;
  animation: ${fadeInOut} 2s ease forwards;
  background: rgba(0, 0, 0, 0.85);
  border: 1px solid rgba(245, 166, 35, 0.4);
  border-radius: 8px;
  color: #f5a623;
  font-size: ${({ $variant }) => ($variant === "overlay" ? "13px" : "14px")};
  font-weight: 500;
  gap: 6px;
  left: 50%;
  padding: 40px 40px;
  pointer-events: none;
  position: absolute;
  top: 50%;
  white-space: nowrap;
  z-index: 10;
`;

const SliderRow = styled(Box)<{ $variant: SlidersVariant }>`
  padding: ${({ $variant }) => ($variant === "overlay" ? "12px 0" : "16px 0")};

  & + & {
    border-top: 1px solid
      ${({ $variant, theme }) =>
        $variant === "overlay"
          ? "rgba(255, 255, 255, 0.15)"
          : theme.colors.chalkAlpha20};
  }
`;
